import React, { useState, useEffect } from 'react';
import { createId } from '@paralleldrive/cuid2';
import { Switch } from '@headlessui/react';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dropdown, Input } from '@/shared';
import { houseApi, type CreateHouseData } from '../api';
import { useHouseMutations } from '../hooks';
import toast from 'react-hot-toast';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  availabilityOptions,
  currencyOptions,
  furnishedOptions,
  houseTypes,
  sizeUnits,
} from '../data';

// Fix for Leaflet default icon not showing
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface AddHouseFormProps {
  onClose: () => void;
}

// ✅ Full validation schema
const schema = yup.object({
  houseName: yup.string().required('House Name is required'),
  houseType: yup.string().required('House Type is required'),
  bedrooms: yup
    .number()
    .typeError('Number of Bedrooms must be a number')
    .required('Number of Bedrooms is required')
    .min(0),
  bathrooms: yup
    .number()
    .typeError('Number of Bathrooms must be a number')
    .required('Number of Bathrooms is required')
    .min(0),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  zip: yup
    .string()
    .matches(/^[0-9]+$/, 'ZIP must be numbers only')
    .length(5, 'ZIP must be exactly 5 digits')
    .required('ZIP is required'),
  area: yup
    .number()
    .typeError('Area must be a number')
    .required('Area is required'),
  size: yup.string().required('Size is required'),
  furnished: yup.string().required('Furnished status is required'),
  price: yup
    .number()
    .typeError('Price must be a number')
    .required('Price is required'),
  currency: yup.string().required('Currency is required'),
  availability: yup.string().required('Availability status is required'),
  listingDate: yup
    .date()
    .typeError('Please select a valid date')
    .required('Listing Date is required'),
  builtYear: yup
    .number()
    .typeError('Built Year must be a number')
    .min(1800, 'Built year seems too old')
    .max(new Date().getFullYear(), 'Built year cannot be in the future')
    .notRequired(),
  images: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one image is required')
    .required('At least one image is required'),
});

// Component to handle map clicks and updates
const LocationMarker = ({
  position,
  setPosition,
  onLocationSelect,
}: {
  position: [number, number] | null;
  setPosition: (pos: [number, number]) => void;
  onLocationSelect: (lat: number, lng: number) => void;
}) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return position === null ? null : <Marker position={position} />;
};

export const AddHouseForm: React.FC<AddHouseFormProps> = ({ onClose }) => {
  const [step, setStep] = useState<number>(1);
  const [parkingAvailable, setParkingAvailable] = useState<boolean>(false);
  const [mapPosition, setMapPosition] = useState<[number, number] | null>(null);
  const [houseId, setHouseId] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const isDraftRef = React.useRef(false); // Track if saving as draft
  const [uploadStatuses, setUploadStatuses] = useState<
    Record<
      string,
      { status: 'Idle' | 'Uploading' | 'Success' | 'Error'; path?: string }
    >
  >({});

  useEffect(() => {
    setHouseId(createId());
  }, []);

  const formRef = React.useRef<HTMLDivElement>(null);

  // ✅ Scroll to top on step change
  useEffect(() => {
    if (formRef.current) {
      // Find the scrollable container (ModalWrapper's content div)
      const scrollableContainer = formRef.current.closest('.overflow-y-auto');
      if (scrollableContainer) {
        scrollableContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [step]);

  const { createHouse } = useHouseMutations();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'all', // Validate on change and blur to clear errors immediately
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  // Sync uploaded images with form state for validation
  useEffect(() => {
    const imagePaths = Object.values(uploadStatuses)
      .filter((s) => (s.status === 'Success' && s.path))
      .map((s) => s.path as string);
    setValue('images', imagePaths, { shouldValidate: true });
  }, [uploadStatuses, setValue]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const remainingSlots = 5 - selectedFiles.length;
      const newFiles = files.slice(0, remainingSlots);

      if (files.length > remainingSlots) {
        toast.error(
          `You can only upload up to 5 images. Adding first ${remainingSlots} files.`,
        );
      }

      setSelectedFiles((prev) => [...prev, ...newFiles]);

      const newStatuses: Record<
        string,
        { status: 'Idle' | 'Uploading' | 'Success' | 'Error' }
      > = {};
      newFiles.forEach((file) => {
        newStatuses[file.name] = { status: 'Idle' };
      });
      setUploadStatuses((prev) => ({ ...prev, ...newStatuses }));
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Uploading' },
      }));

      const { signedUrl, path } = await houseApi.getUploadUrl(
        houseId,
        file.name,
        file.type,
      );

      await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Success', path },
      }));

      toast.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error(error);
      setUploadStatuses((prev) => ({
        ...prev,
        [file.name]: { status: 'Error' },
      }));
      toast.error(`Failed to upload ${file.name}`);
    }
  };

  const stepsHeading = [
    'Basic Info',
    'Location Details',
    'House Specifications',
    'Pricing & Availability',
    'Media',
  ];

  // Validate current step before moving to next
  const nextStep = async () => {
    let fieldsToValidate: string[] = [];

    switch (step) {
      case 1:
        fieldsToValidate = ['houseName', 'houseType', 'bedrooms', 'bathrooms'];
        break;
      case 2:
        fieldsToValidate = ['address', 'city', 'state', 'zip'];
        break;
      case 3:
        fieldsToValidate = ['area', 'size', 'furnished'];
        break;
      case 4:
        fieldsToValidate = ['price', 'currency', 'availability', 'listingDate'];
        break;
      case 5:
        // Step 5 (media) is optional, no validation needed
        setStep((prev) => Math.min(prev + 1, 5));
        return;
      default:
        setStep((prev) => Math.min(prev + 1, 5));
        return;
    }

    // Trigger validation for the current step's fields
    const isValid = await trigger(
      fieldsToValidate as (
        | 'houseName'
        | 'houseType'
        | 'bedrooms'
        | 'bathrooms'
        | 'address'
        | 'city'
        | 'state'
        | 'zip'
        | 'area'
        | 'size'
        | 'furnished'
        | 'price'
        | 'currency'
        | 'availability'
        | 'listingDate'
      )[],
    );

    if (isValid) {
      setStep((prev) => Math.min(prev + 1, 5));
    }
    // Errors will be displayed below the fields automatically
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Function to reverse geocode coordinates
  const reverseGeocode = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      );
      const data = await response.json();

      if (data.address) {
        // Auto-fill form fields

        // Construct a more complete address string
        const parts = [];
        if (data.address.house_number) parts.push(data.address.house_number);
        if (data.address.road) parts.push(data.address.road);
        if (data.address.neighbourhood) parts.push(data.address.neighbourhood);
        if (data.address.suburb) parts.push(data.address.suburb);

        const fullAddress = parts.join(', ');
        if (fullAddress) {
          setValue('address', fullAddress);
        } else if (data.display_name) {
          // Fallback to display_name if specific fields are missing, but try to keep it short
          const displayNameParts = data.display_name.split(', ');
          // Take the first 2-3 parts as the street address
          setValue('address', displayNameParts.slice(0, 3).join(', '));
        }

        if (
          data.address.city ||
          data.address.town ||
          data.address.village ||
          data.address.county
        ) {
          setValue(
            'city',
            data.address.city ||
              data.address.town ||
              data.address.village ||
              data.address.county,
          );
        }
        if (data.address.state) {
          setValue('state', data.address.state);
        }
        if (data.address.postcode) {
          setValue('zip', data.address.postcode);
        }
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      toast.error('Failed to fetch address details');
    }
  };

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setMapPosition([latitude, longitude]);
          reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Failed to get current location');
        },
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const imagePaths = Object.values(uploadStatuses)
        .filter((s) => s.status === 'Success' && s.path)
        .map((s) => s.path as string);

      const payload: CreateHouseData = {
        name: data.houseName,
        type: data.houseType,
        address: data.address,
        city: data.city,
        state: data.state,
        zipCode: data.zip.toString(),
        description: data.description,
        capacity: Number(data.bedrooms),
        bedrooms: Number(data.bedrooms),
        bathrooms: Number(data.bathrooms),
        area: data.area.toString(),
        size: data.size,
        builtYear: data.builtYear?.toString(),
        isParkingAvailable: parkingAvailable,
        isFurnished: data.furnished === 'Furnished', // Map from 'Yes'/'No' or whatever dropdown values
        price: Number(data.price),
        currency: data.currency,
        seats: 0, // Default as not in form
        status: isDraftRef.current
          ? 'DRAFT'
          : data.availability === 'ACTIVE'
          ? 'ACTIVE'
          : 'INACTIVE',
        amenities: data.amenities || [],
        images: imagePaths,
        listingDate: new Date(data.listingDate).toISOString(),
        id: houseId,
      };

      console.log('Final Payload:', payload);

      await createHouse.mutateAsync(payload);
      toast.success('House created successfully!');
      onClose();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to create house');
    }
  };

  return (
    <div ref={formRef} className="bg-white rounded-4xl p-6 max-w-lg w-full min-h-50 mx-4">
      <h3 className="text-lg md:text-3xl font-bold mb-2 text-text-primary">Add House</h3>
      <p className="text-gray-600 mb-4 md:mb-8 text-base md:text-2xl text-text-secondary">
        Manage your properties with ease
      </p>

      {/* Step Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-2xl font-bold text-text-primary">
          Step {step}: {stepsHeading[step - 1]}
        </h3>
        <span className="text-lg md:text-xl text-text-secondary">{step}/5</span>
      </div>

      {/* Form */}
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1 */}
        {step === 1 && (
          <>
            <div className="mb-4">
              <Input
                label="House Name"
                className="text-base md:!text-xl"
                {...register('houseName')}
                error={errors.houseName?.message}
              />
            </div>
            <div className="mb-4">
              <Controller
                name="houseType"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="House Type"
                    buttonClassName="!w-full"
                    options={houseTypes}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.houseType && (
                <p className="text-red-500 text-sm">
                  {errors.houseType.message}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Input
                label="Number of Bedrooms"
                type="number"
                {...register('bedrooms')}
                error={errors.bedrooms?.message}
              />
            </div>
            <div className="mb-4">
              <Input
                label="Number of Bathrooms"
                type="number"
                {...register('bathrooms')}
                error={errors.bathrooms?.message}
              />
            </div>
          </>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <>
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <Input
                  label="Address"
                  placeholder="Flat # 1, St 2, XYZ Town"
                  {...register('address')}
                  error={errors.address?.message}
                />
                <div
                  className="w-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={handleCurrentLocation}
                  title="Use current location"
                >
                  <img src="./assets/icons/maps.svg" alt="" />
                </div>
              </div>
            </div>
            <div className="mb-4">
              <Input
                label="City"
                {...register('city')}
                error={errors.city?.message}
              />
            </div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <Input
                label="State / Province"
                {...register('state')}
                error={errors.state?.message}
              />

              <Input
                label="ZIP Code"
                type="text"
                {...register('zip')}
                error={errors.zip?.message}
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor=""
                className="block font-medium text-gray-700 mb-2 high-contrast:text-gray-900"
              >
                Pin on Map
              </label>
              <div className="w-full h-40 rounded-lg overflow-hidden border border-gray-300">
                <MapContainer
                  center={mapPosition || [51.505, -0.09]} // Default to London or user location
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <LocationMarker
                    position={mapPosition}
                    setPosition={setMapPosition}
                    onLocationSelect={reverseGeocode}
                  />
                </MapContainer>
              </div>
              {/* {errors.city && (
                <p className="text-red-500 text-sm">{errors.city.message}</p>
              )} */}
            </div>
          </>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <Input
                label="Area"
                type="number"
                {...register('area')}
                error={errors.area?.message}
              />
              <Controller
                name="size"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="Size"
                    options={sizeUnits}
                    buttonClassName="!w-full"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.size && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.size.message}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Controller
                name="furnished"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="Furnished Status"
                    options={furnishedOptions}
                    buttonClassName="!w-full"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.furnished && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.furnished.message}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Input
                label="Built Year (optional)"
                type="number"
                {...register('builtYear')}
                error={errors.builtYear?.message}
              />
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className="text-text-primary">Parking Availability</span>
              <Switch
                checked={parkingAvailable}
                onChange={setParkingAvailable}
                className={`${
                  parkingAvailable ? 'bg-[#5761EB]' : 'bg-gray-300'
                } relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span
                  className={`${
                    parkingAvailable ? 'translate-x-6' : 'translate-x-1'
                  } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>
          </>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <>
            <div className="mb-4">
              <Input
                label="Price"
                type="number"
                {...register('price')}
                error={errors.price?.message}
              />
            </div>
            <div className="mb-4">
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="Currency"
                    options={currencyOptions}
                    buttonClassName="!w-full"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.currency && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.currency.message}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Controller
                name="availability"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    label="Availability Status"
                    options={availabilityOptions}
                    buttonClassName="!w-full"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              {errors.availability && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.availability.message}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Input
                label="Listing Date"
                type="date"
                {...register('listingDate')}
                error={errors.listingDate?.message}
              />
            </div>
          </>
        )}

        {/* Step 5 */}
        {step === 5 && (
          <>
            <label className="block text-text-primary mb-2">Upload Media</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer mb-4">
                <div className="flex flex-col gap-4 items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 13.5V3"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.25 13.5V19.5H3.75V13.5"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.25 6.75L12 3L15.75 6.75"
                      stroke="#141414"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="text-sm">Click to choose files or drag here</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Max 5 images. Size limit: 10MB each
                  </p>
                </div>
              </div>
            </label>

            {/* Selected Images List */}
            <div className="space-y-3">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded overflow-hidden bg-gray-200">
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium truncate max-w-[150px]">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>

                  {uploadStatuses[file.name]?.status === 'Success' ? (
                    <span className="text-green-600 text-sm font-medium px-3 py-1 bg-green-50 rounded-full">
                      Uploaded
                    </span>
                  ) : (
                    <Button
                      variant={
                        uploadStatuses[file.name]?.status === 'Uploading'
                          ? 'secondary'
                          : 'primary'
                      }
                      size="sm"
                      type="button"
                      disabled={
                        uploadStatuses[file.name]?.status === 'Uploading'
                      }
                      onClick={() => handleUpload(file)}
                    >
                      {uploadStatuses[file.name]?.status === 'Uploading'
                        ? 'Uploading...'
                        : 'Upload'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Validation Error for Images (Step 5) */}
        {step === 5 && errors.images && (
          <p className="text-red-500 text-sm mb-4 px-1">
            {errors.images.message as string}
          </p>
        )}

        {/* Navigation Buttons (unchanged) */}
        <div className="pt-6">
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={nextStep}
            className={`w-full mb-4 ${
              step === stepsHeading.length ? '!hidden' : '!block'
            }`}
          >
            Continue
          </Button>

          <Button
            variant="primary"
            size="lg"
            type="submit"
            className={`w-full mb-4 ${
              step !== stepsHeading.length ? '!hidden' : '!block'
            }`}
          >
            Add House
          </Button>

          <Button
            variant="secondary"
            size="lg"
            type="submit"
            onClick={() => (isDraftRef.current = true)}
            className={`w-full mb-4 !border-[#5761EB] ${
              step !== stepsHeading.length ? '!hidden' : '!block'
            }`}
          >
            Save as Draft
          </Button>

          <Button
            variant="secondary"
            size="lg"
            type="button"
            onClick={prevStep}
            className={`w-full ${step === 1 ? '!hidden' : '!block'}`}
          >
            Back to {step !== 1 && stepsHeading[step - 2]}
          </Button>
        </div>
      </form>
    </div>
  );
};
